<script setup lang="ts">
    import { ref, watch } from 'vue'
    import { MdEditor, config, type ToolbarNames } from 'md-editor-v3'
    import 'md-editor-v3/lib/style.css'
    import { useTheme } from 'composables'

    const noStyle = { light: '', dark: '' }

    config({
        editorExtensions: {
            highlight: {
                css: {
                    a11y: noStyle,
                    atom: noStyle,
                    github: noStyle,
                    gradient: noStyle,
                    kimbie: noStyle,
                    paraiso: noStyle,
                    qtcreator: noStyle,
                    stackoverflow: noStyle,
                },
            },
        },
    })

    const props = withDefaults(
        defineProps<{
            modelValue?: string
            label?: string
            placeholder?: string
            readonly?: boolean
            language?: 'en-US' | 'it-IT' | 'es-ES' | 'fr-FR' | 'de-DE'
            height?: string
            minHeight?: string
            maxHeight?: string
            preview?: boolean
            toolbars?: ToolbarNames[]
            showCodeRowNumber?: boolean
            autoFocus?: boolean
            previewTheme?:
                | 'default'
                | 'github'
                | 'vuepress'
                | 'mk-cute'
                | 'smart-blue'
                | 'cyanosis'
            codeTheme?:
                | 'atom'
                | 'a11y'
                | 'github'
                | 'gradient'
                | 'kimbie'
                | 'paraiso'
                | 'qtcreator'
                | 'stackoverflow'
        }>(),
        {
            modelValue: '',
            label: '',
            placeholder: '',
            readonly: false,
            language: 'en-US',
            height: undefined,
            minHeight: '400px',
            maxHeight: undefined,
            preview: true,
            toolbars: () => [
                'bold',
                'underline',
                'italic',
                '-',
                'title',
                'strikeThrough',
                'sub',
                'sup',
                'quote',
                'unorderedList',
                'orderedList',
                'task',
                '-',
                'codeRow',
                'code',
                'link',
                // 'image',
                'table',
                'mermaid',
                'katex',
                '-',
                'revoke',
                'next',
                // 'save',
                '=',
                'pageFullscreen',
                // 'fullscreen',
                'preview',
                'previewOnly',
                // 'htmlPreview',
                // 'catalog',
                // 'github',
            ],
            showCodeRowNumber: true,
            autoFocus: false,
            previewTheme: 'github',
            codeTheme: 'github',
        },
    )

    const { isDarkTheme } = useTheme()

    const emit = defineEmits<{
        'update:modelValue': [value: string]
    }>()

    const localValue = ref(props.modelValue)

    watch(
        () => props.modelValue,
        (newValue) => {
            if (newValue !== localValue.value) {
                localValue.value = newValue
            }
        },
    )

    const handleChange = (value: string) => {
        localValue.value = value
        emit('update:modelValue', value)
    }

    const editorId = `md-editor-${Math.random().toString(36).slice(2, 11)}`
</script>

<template>
    <div class="pk-editor-markdown">
        <span v-if="label" class="pk-editor-markdown__label">
            {{ label }}
        </span>
        <div class="pk-editor-markdown__wrapper">
            <MdEditor
                :id="editorId"
                class="wysiwyg"
                :model-value="localValue"
                :placeholder
                :language
                :preview
                :toolbars
                :show-code-row-number
                :auto-focus
                :read-only="readonly"
                :theme="isDarkTheme ? 'dark' : 'light'"
                :preview-theme
                :style="{
                    height: height,
                    minHeight: minHeight,
                    maxHeight: maxHeight,
                }"
                @update:model-value="handleChange" />
        </div>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-markdown {
            &:not(.mb-0) {
                margin-bottom: var(--input-gap);
            }

            &__label {
                display: var(--input-label-display);
                font-size: var(--input-label-font-size);
                font-weight: var(--input-label-font-weight);
                color: var(--input-label-color);
                margin: 0 0 var(--input-label-gap) 0;
                white-space: var(--input-label-white-space);
                text-overflow: var(--input-label-text-overflow);
                overflow: var(--input-label-overflow);
                max-width: var(--input-label-max-width);
            }
        }
    }

    // md-editor ships its stylesheet unlayered, and an unlayered rule beats
    // every layer whatever the layered selector's specificity. These three
    // overrides used to live in `volver.components`, so they shipped and did
    // nothing: the panes kept the vendor sizes of 16px and 14px. They stay
    // outside the layer, scoped to the block so no other md-editor instance
    // is affected.
    .pk-editor-markdown {
        // The rendered prompt at the size the field shows it, down from the
        // 16px of the vendor theme
        .md-editor-preview {
            font-size: 14px;
        }

        // Source pane: monospace carries at a smaller size, and this matches
        // the other code surfaces in the app
        .md-editor .cm-editor {
            font-size: 12px;
        }

        // Tighter than the 1.4em/0.8em of the theme, which spaces headings
        // like a document rather than like a prompt
        div.github-theme {
            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                margin-block: 1em 0.5em;
            }
        }
    }
</style>
